import React, { useEffect, useRef } from 'react';

import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import styles from './dropdownList.module.scss';

export type Option = {
  value: string | number;
  label: string;
};

export type DropdownListProps = {
  options: Option[];
  dropdownMobileSide?: 'left' | 'right' | 'left-fit';
  onSelectOption: (selectedOption: Option) => void;
  minScrollableLength: number;
  selectedOption?: Option;
  top?: 'full' | number;
  marginTop?: number;
  overrideWidth?: boolean;
};

export const DropdownList: React.FC<DropdownListProps> = ({
  options,
  selectedOption,
  dropdownMobileSide = 'left',
  onSelectOption,
  minScrollableLength,
  top = 'full',
  marginTop = 0,
  overrideWidth,
}) => {
  const showDropdownScrollBar =
    options && options.length >= minScrollableLength;
  const isMobile = useIsMobile();

  const topValue = top === 'full' ? '100%' : `${top}px`;
  const marginTopValue = `${marginTop}px`;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { kiosk, kioskMode } = useKioskManager();

  useEffect(() => {
    //calculating max width of dropdown by finding the longest string
    if (overrideWidth && !isMobile && dropdownRef.current) {
      const maxWidth = options.reduce(
        (max, option) => Math.max(max, option.label.length),
        0,
      );
      dropdownRef.current.style.minWidth = `${maxWidth * 10}px`;
    }
  }, [options, overrideWidth, isMobile]);
  return (
    <div
      ref={dropdownRef}
      className={`${styles.dropdown} ${styles[dropdownMobileSide]} ${
        isMobile && overrideWidth && styles.mobileFullWidth
      } ${kiosk && kioskMode && styles[kiosk.model]}`}
      style={{ marginTop: marginTopValue, top: topValue }}
    >
      <ul
        role="menu"
        className={`
              ${styles.dropdown__list}
              ${showDropdownScrollBar && styles.scrollbar_active}
            `}
      >
        {options?.map((option) => (
          <li
            tabIndex={0}
            role="menuitem"
            key={option.value}
            onClick={() => onSelectOption(option)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSelectOption(option);
              }
            }}
            className={`
                  ${styles.dropdown__item}
                  ${option.value === selectedOption?.value && styles.selected}
                `}
          >
            <div className={styles.label}>{option?.label}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};
